import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useIsLoggedOutHomepageRedesignV2, useSettingLoggedOutHomepageRedesignV2 } from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutHomepageRedesignV2";
import { BlogTile } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BlogCarousel/__internal__/BlogTile";
import { Carousel } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/Arrow";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { useQuery as useBlogCarouselQuery } from "@faire/web-api--source/api/logged-out-homepage/blog-carousel/get-hooks";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import React from "react";
export var BlogCarousel = function() {
    var _data_carousel_tiles;
    var isHomepageRedesignV2 = useIsLoggedOutHomepageRedesignV2();
    var isMobile = useViewport().isMobile;
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var ref = useDoActionWhenVisible(function() {
        return trackLoggedOutRetailersLoggedOutHomeRedesignView("blog_carousel", "".concat(homepageRedesignVariant));
    });
    var data = useBlogCarouselQuery({
        gcTime: Infinity,
        staleTime: Infinity
    }).data;
    if (!(data === null || data === void 0 ? void 0 : (_data_carousel_tiles = data.carousel_tiles) === null || _data_carousel_tiles === void 0 ? void 0 : _data_carousel_tiles.length)) {
        return null;
    }
    var title = data.title, cta_link_url = data.cta_link_url, cta_link_text = data.cta_link_text, carousel_tiles = data.carousel_tiles;
    return /*#__PURE__*/ _jsxs(Column, {
        className: isHomepageRedesignV2 ? "mx-4 my-12 md:mx-8 lg:mx-12 2xl:mx-20" : "w-full max-w-[1088px] py-12 pl-8 pr-0 lg:mx-auto lg:py-[90px] lg:pr-0",
        "data-test-id": "blog-carousel",
        gap: "4x",
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: isHomepageRedesignV2 ? {
                            mobileAndAbove: "pageHeaderSerifRegular",
                            xLargeAndAbove: "displaySSerifRegular",
                            xxLargeDesktop: "displayMSerifRegular"
                        } : {
                            mobileAndAbove: "displaySSerifRegular",
                            desktopAndAbove: "displayMSerifRegular"
                        },
                        style: {
                            paddingRight: "".concat(getSpacing("4x")),
                            overflowWrap: "anywhere"
                        },
                        children: title
                    }),
                    /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                        desktopAndAbove: /*#__PURE__*/ _jsx(CTADesktopAndAbove, {
                            ctaLink: cta_link_url,
                            ctaLinkText: cta_link_text
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Carousel, {
                items: carousel_tiles !== null && carousel_tiles !== void 0 ? carousel_tiles : [],
                renderItem: function(blog) {
                    return /*#__PURE__*/ _jsx(BlogTile, {
                        blogTile: blog
                    });
                },
                freeFlow: !isHomepageRedesignV2 || isMobile,
                itemsPerSlide: isHomepageRedesignV2 ? 4 : undefined,
                itemGap: 16,
                showNavigationArrows: false,
                loop: false,
                className: "pl-0",
                onVisible: function() {
                    trackLoggedOutRetailersLoggedOutHomeRedesignView("blog_carousel-onscroll", "".concat(homepageRedesignVariant));
                }
            }),
            /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                tabletAndBelow: /*#__PURE__*/ _jsx(CTATabletAndBelow, {
                    ctaLink: cta_link_url,
                    ctaLinkText: cta_link_text
                })
            })
        ]
    });
};
var CTADesktopAndAbove = function(param) {
    var ctaLink = param.ctaLink, ctaLinkText = param.ctaLinkText;
    var isHomepageRedesignV2 = useIsLoggedOutHomepageRedesignV2();
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    return /*#__PURE__*/ _jsxs(Button, {
        variant: "tertiary",
        linkTo: ctaLink,
        className: isHomepageRedesignV2 ? "group" : "group pr-4",
        onClick: function() {
            trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick("blog-carousel-cta", ctaLink !== null && ctaLink !== void 0 ? ctaLink : "", "".concat(homepageRedesignVariant));
        },
        children: [
            ctaLinkText,
            isHomepageRedesignV2 ? null : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        width: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Arrow, {
                        direction: "right",
                        className: "!mr-8 transition-all duration-500 ease-in-out group-hover:translate-x-2.5"
                    })
                ]
            })
        ]
    });
};
var CTATabletAndBelow = function(param) {
    var ctaLink = param.ctaLink, ctaLinkText = param.ctaLinkText;
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    return /*#__PURE__*/ _jsx(Button, {
        variant: "secondary",
        className: "hover:!bg-core-surface-default !bg-transparent",
        style: {
            width: "fit-content"
        },
        linkTo: ctaLink,
        onClick: function() {
            trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick("blog-carousel-cta", ctaLink !== null && ctaLink !== void 0 ? ctaLink : "", "".concat(homepageRedesignVariant));
        },
        children: ctaLinkText
    });
};
